import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as api from "../api/api";
import "./AdminEdit.css";
import LoadingSpinner from "./LoadingSpinner";

const AdminContentEdit = () => {
  const navigate = useNavigate();
  const { locationSlug } = useParams();
  const [content, setContent] = useState(null);
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        const res = await api.getContent(locationSlug);
        if (mounted && res) setContent(res);
      } catch (err) {
        console.error("Failed to load content for", locationSlug, err);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, [locationSlug]);

  const handleSave = async () => {
    try {
      await api.updateContent(locationSlug, content);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Failed to save content:", err);
      alert("Erreur lors de la sauvegarde du contenu. Voir la console.");
    }
  };

  const addValue = () => {
    setContent({
      ...content,
      values: [
        ...content.values,
        { title: "Nouvelle Valeur", description: "Description" },
      ],
    });
  };

  const removeValue = (index) => {
    setContent({
      ...content,
      values: content.values.filter((_, i) => i !== index),
    });
  };

  const updateValue = (index, field, value) => {
    const newValues = [...content.values];
    newValues[index][field] = value;
    setContent({ ...content, values: newValues });
  };

  const addService = () => {
    setContent({
      ...content,
      services: [...content.services, "Nouveau service"],
    });
  };

  const removeService = (index) => {
    setContent({
      ...content,
      services: content.services.filter((_, i) => i !== index),
    });
  };

  const updateService = (index, value) => {
    const newServices = [...content.services];
    newServices[index] = value;
    setContent({ ...content, services: newServices });
  };

  if (loading || !content) return <LoadingSpinner />;

  return (
    <div className="admin-edit-page">
      <div className="admin-header">
        <div className="container">
          <button
            className="back-btn"
            onClick={() =>
              navigate("/admin/dashboard", {
                state: { selectedLocation: locationSlug },
              })
            }
          >
            <ArrowLeft size={20} /> Retour au dashboard
          </button>
          <div className="admin-header-content">
            <h1 className="heading-md">Modifier - Contenu</h1>
            <button
              className="btn-primary"
              onClick={handleSave}
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <Save size={18} /> {saved ? "✓ Sauvegardé" : "Sauvegarder"}
            </button>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="container">
          {/* Mission Section */}
          <div className="edit-section">
            <h2 className="heading-sm">Mission</h2>
            <div className="form-group">
              <label className="form-label">Titre</label>
              <input
                type="text"
                className="form-input"
                value={content.mission.title}
                onChange={(e) =>
                  setContent({
                    ...content,
                    mission: { ...content.mission, title: e.target.value },
                  })
                }
              />
            </div>
            <div className="form-group">
              <label className="form-label">Contenu</label>
              <textarea
                className="form-textarea"
                rows="5"
                value={content.mission.content}
                onChange={(e) =>
                  setContent({
                    ...content,
                    mission: { ...content.mission, content: e.target.value },
                  })
                }
              />
            </div>
          </div>

          {/* Values Section */}
          <div className="edit-section">
            <div className="section-header">
              <h2 className="heading-sm">Valeurs</h2>
              <button className="btn-secondary btn-small" onClick={addValue}>
                <Plus size={16} /> Ajouter une valeur
              </button>
            </div>

            {content.values.map((value, index) => (
              <div key={index} className="list-item-edit">
                <div className="form-group">
                  <label className="form-label">Titre</label>
                  <input
                    type="text"
                    className="form-input"
                    value={value.title}
                    onChange={(e) =>
                      updateValue(index, "title", e.target.value)
                    }
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Description</label>
                  <textarea
                    className="form-textarea"
                    rows="3"
                    value={value.description}
                    onChange={(e) =>
                      updateValue(index, "description", e.target.value)
                    }
                  />
                </div>
                <button
                  className="btn-delete"
                  onClick={() => removeValue(index)}
                >
                  <Trash2 size={16} /> Supprimer
                </button>
              </div>
            ))}
          </div>

          {/* Services Section */}
          <div className="edit-section">
            <div className="section-header">
              <h2 className="heading-sm">Spécialités</h2>
              <button className="btn-secondary btn-small" onClick={addService}>
                <Plus size={16} /> Ajouter une spécialité
              </button>
            </div>

            {content.services.map((service, index) => (
              <div key={index} className="list-item-simple">
                <input
                  type="text"
                  className="form-input"
                  value={service}
                  onChange={(e) => updateService(index, e.target.value)}
                />
                <button
                  className="btn-icon-delete"
                  onClick={() => removeService(index)}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}
          </div>

          {/* Save Button Bottom */}
          <div className="bottom-actions">
            <button className="btn-primary btn-large" onClick={handleSave}>
              <Save size={20} /> Enregistrer toutes les modifications
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AdminContentEdit;
